import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { toast } from "../lib/toast";
import {
  fetchOwnerDrivers,
  unlinkDriverFromOwner,
  linkExistingDriverToOwner,
  registerDriverAndLinkToOwner,
} from "../api/ownerDriversApi";
import { fetchOwnerProfile } from "../api/ownerCarsApi";

/* ───────── Helpers ───────── */

const normalizePhoneInput = (value) =>
  String(value || "").replace(/\D/g, "").slice(0, 10);

const getErrorMessage = (
  err,
  fallback = "Something went wrong. Please try again."
) => {
  const raw = err?.response?.data;
  return (
    (typeof raw === "string" && raw) ||
    raw?.message ||
    raw?.error ||
    raw?.detail ||
    err?.message ||
    fallback
  );
};

const normalizeStatus = (value) =>
  String(value || "")
    .trim()
    .replace(/[\s-]+/g, "_")
    .toUpperCase();

const getDriverComplianceRaw = (driver) =>
  driver?.complianceStatus ||
  driver?.verificationStatus ||
  driver?.kycStatus ||
  driver?.driverCompliance?.status ||
  driver?.compliance?.status ||
  "";

const getDriverComplianceMeta = (driver) => {
  const raw = normalizeStatus(getDriverComplianceRaw(driver));

  if (raw === "VERIFIED" || raw === "APPROVED") {
    return {
      label: "Verified",
      tone: "bg-emerald-100 text-emerald-800 border-emerald-200",
      verified: true,
    };
  }

  if (
    raw === "PENDING" ||
    raw === "SUBMITTED" ||
    raw === "UNDER_REVIEW" ||
    raw === "IN_REVIEW"
  ) {
    return {
      label: "Pending review",
      tone: "bg-amber-100 text-amber-800 border-amber-200",
      verified: false,
    };
  }

  if (
    raw === "ACTION_REQUIRED" ||
    raw === "REJECTED" ||
    raw === "DECLINED" ||
    raw === "FAILED" ||
    raw === "EXPIRED"
  ) {
    return {
      label:
        raw === "ACTION_REQUIRED"
          ? "Action required"
          : raw === "EXPIRED"
          ? "Expired"
          : "Rejected",
      tone: "bg-rose-100 text-rose-800 border-rose-200",
      verified: false,
    };
  }

  return {
    label: "Not submitted",
    tone: "bg-slate-100 text-slate-700 border-slate-200",
    verified: false,
  };
};

const isDriverBlocked = (driver) =>
  driver?.blocked === true ||
  driver?.isBlocked === true ||
  normalizeStatus(driver?.status) === "BLOCKED" ||
  normalizeStatus(driver?.availabilityStatus) === "BLOCKED";

const getEligibilityMeta = (driver) => {
  const compliance = getDriverComplianceMeta(driver);
  const blocked = isDriverBlocked(driver);

  const explicit =
    typeof driver?.eligibleForAssignment === "boolean"
      ? driver.eligibleForAssignment
      : typeof driver?.assignmentEligible === "boolean"
      ? driver.assignmentEligible
      : typeof driver?.eligibleForBooking === "boolean"
      ? driver.eligibleForBooking
      : typeof driver?.openForBooking === "boolean"
      ? driver.openForBooking
      : null;

  const eligible =
    explicit != null ? explicit : !blocked && compliance.verified === true;

  if (blocked) {
    return {
      eligible: false,
      label: "Blocked",
      tone: "bg-rose-100 text-rose-800 border-rose-200",
      reason: "Driver is blocked",
    };
  }

  if (!compliance.verified) {
    return {
      eligible: false,
      label: "Not eligible",
      tone: "bg-amber-100 text-amber-800 border-amber-200",
      reason: "Compliance not verified",
    };
  }

  if (eligible) {
    return {
      eligible: true,
      label: "Eligible",
      tone: "bg-indigo-100 text-indigo-800 border-indigo-200",
      reason: "",
    };
  }

  return {
    eligible: false,
    label: "Not eligible",
    tone: "bg-slate-100 text-slate-700 border-slate-200",
    reason: "Not open for assignment",
  };
};

const Pill = ({ label, tone }) => (
  <span
    className={`inline-flex items-center rounded-[8px] border px-2.5 py-1 text-[11px] font-semibold leading-none ${tone}`}
  >
    {label}
  </span>
);

const ManageDrivers = () => {
  const navigate = useNavigate();

  const [owner, setOwner] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [removingId, setRemovingId] = useState(null);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    name: "",
    phoneNumber: "",
    email: "",
    licenseNumber: "",
  });

  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkPhone, setLinkPhone] = useState("");
  const [linkSubmitting, setLinkSubmitting] = useState(false);

  const totalDrivers = drivers.length;

  const verifiedCount = useMemo(
    () => drivers.filter((d) => getDriverComplianceMeta(d).verified).length,
    [drivers]
  );

  const eligibleCount = useMemo(
    () => drivers.filter((d) => getEligibilityMeta(d).eligible).length,
    [drivers]
  );

  const loadOwnerAndDrivers = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setError("");

      const prof = await fetchOwnerProfile();
      if (!prof?.id) {
        throw new Error("Owner profile missing id");
      }

      setOwner(prof);

      const list = await fetchOwnerDrivers(prof.id);
      setDrivers(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Error loading drivers:", err);
      const message = getErrorMessage(
        err,
        "We couldn't load your drivers right now. Please try again."
      );
      setError(message);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    loadOwnerAndDrivers();
  }, []);

  useEffect(() => {
    const hasModal = showInviteModal || showLinkModal;
    const prev = document.body.style.overflow;
    if (hasModal) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showInviteModal, showLinkModal]);

  const handleBack = () => {
    navigate("/owner/dashboard");
  };

  const handleOpenInvite = () => {
    setInviteForm({
      name: "",
      phoneNumber: "",
      email: "",
      licenseNumber: "",
    });
    setShowInviteModal(true);
  };

  const handleInviteChange = (field, value) => {
    setInviteForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetInviteState = () => {
    setShowInviteModal(false);
    setInviteForm({
      name: "",
      phoneNumber: "",
      email: "",
      licenseNumber: "",
    });
  };

  const handleInviteSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!owner?.id) {
      toast.error("Owner profile not loaded yet.");
      return;
    }

    const name = inviteForm.name.trim();
    const email = inviteForm.email.trim();
    const licenseNumber = inviteForm.licenseNumber.trim().toUpperCase();
    const phoneNumber = normalizePhoneInput(inviteForm.phoneNumber || "");

    if (!name || !phoneNumber || !licenseNumber) {
      toast.error("Please fill name, phone number and license number.");
      return;
    }

    if (phoneNumber.length !== 10) {
      toast.error("Phone number must be exactly 10 digits.");
      return;
    }

    try {
      setInviteSubmitting(true);

      await registerDriverAndLinkToOwner(owner.id, {
        name,
        phoneNumber,
        email: email || null,
        licenseNumber,
      });

      toast.success("Driver added to your fleet.");
      resetInviteState();
      await loadOwnerAndDrivers({ silent: true });
    } catch (err) {
      console.error("Error inviting driver:", err);
      const status = err?.response?.status;
      const message = getErrorMessage(
        err,
        "We couldn't create this driver profile. Please check the details and try again."
      );
      toast.error(`Invite failed${status ? ` (${status})` : ""}: ${message}`);
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleOpenLink = () => {
    setLinkPhone("");
    setShowLinkModal(true);
  };

  const handleLinkSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!owner?.id) {
      toast.error("Owner profile not loaded yet.");
      return;
    }

    const phoneNumber = normalizePhoneInput(linkPhone || "");

    if (!phoneNumber) {
      toast.error("Please enter a phone number.");
      return;
    }

    if (phoneNumber.length !== 10) {
      toast.error("Please enter a valid 10-digit phone number.");
      return;
    }

    try {
      setLinkSubmitting(true);

      await linkExistingDriverToOwner(owner.id, phoneNumber);

      toast.success("Existing driver linked to your fleet.");
      setShowLinkModal(false);
      setLinkPhone("");
      await loadOwnerAndDrivers({ silent: true });
    } catch (err) {
      console.error("Error linking driver:", err);
      const status = err?.response?.status;
      const message = getErrorMessage(
        err,
        "We couldn't find or link a driver with this phone number."
      );
      toast.error(`Link failed${status ? ` (${status})` : ""}: ${message}`);
    } finally {
      setLinkSubmitting(false);
    }
  };

  const handleRemoveDriver = async (driverId) => {
    if (!driverId) return;

    const confirmRemove = window.confirm(
      "Remove this driver from your fleet? They will no longer appear in your bookings console."
    );
    if (!confirmRemove) return;

    try {
      setRemovingId(driverId);
      await unlinkDriverFromOwner(driverId);
      toast.success("Driver removed from your fleet.");
      setDrivers((prev) => prev.filter((d) => d.id !== driverId));
    } catch (err) {
      console.error("Error unlinking driver:", err);
      const message = getErrorMessage(
        err,
        "We couldn't remove this driver. Please try again."
      );
      toast.error(message);
    } finally {
      setRemovingId(null);
    }
  };

  const ownerName =
    owner?.displayName || owner?.name || owner?.email || "Owner";

  const renderDriverCard = (d, idx) => {
    const compliance = getDriverComplianceMeta(d);
    const eligibility = getEligibilityMeta(d);

    return (
      <div
        key={d.id}
        className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 tabular-nums">
                {idx + 1}
              </span>
              <div className="truncate text-[15px] font-semibold text-slate-900">
                {d.name || "—"}
              </div>
            </div>

            {d.email && (
              <div className="mt-1 break-all text-[11px] text-slate-500">
                {d.email}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => handleRemoveDriver(d.id)}
            disabled={removingId === d.id}
            className="shrink-0 rounded-lg border border-rose-500 px-3 py-1.5 text-[11px] font-medium text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {removingId === d.id ? "Removing…" : "Remove"}
          </button>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 rounded-2xl bg-slate-50 p-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              Contact
            </div>
            <div className="mt-1 text-[13px] text-slate-900 tabular-nums">
              {d.phoneNumber || "—"}
            </div>
          </div>

          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              License
            </div>
            <div className="mt-1 inline-flex items-center rounded-[6px] bg-white px-2 py-[4px] text-[11px] font-medium text-slate-700">
              {d.licenseNumber || "Not provided"}
            </div>
          </div>

          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              Compliance
            </div>
            <div className="mt-1">
              <Pill label={compliance.label} tone={compliance.tone} />
            </div>
          </div>

          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              Booking assignment
            </div>
            <div className="mt-1 flex flex-col items-start gap-1.5">
              <Pill label={eligibility.label} tone={eligibility.tone} />
              {!eligibility.eligible && eligibility.reason && (
                <span className="text-[11px] text-slate-500">
                  {eligibility.reason}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900">
      <Header />

      <main className="flex-1">
        <div className="mx-auto max-w-[1560px] px-4 py-5 sm:px-6 sm:py-6 lg:px-10 2xl:px-12">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                Fleet team
              </p>

              <div className="mt-1 flex flex-col items-start gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
                  Manage drivers
                </h1>

                {totalDrivers > 0 && (
                  <div className="inline-flex max-w-full items-center gap-2 rounded-2xl border border-[#FF2D83] bg-white px-3 py-2 text-[11px] font-medium text-slate-900 sm:px-3.5 sm:py-1.5">
                    <span className="grid h-7 min-w-[44px] place-items-center rounded-xl bg-[#FF2D83]/6 px-2 text-[12px] font-semibold text-[#FF2D83] tabular-nums">
                      {totalDrivers}
                    </span>
                    <span className="truncate text-[12px] font-semibold text-slate-700">
                      active drivers linked
                    </span>
                  </div>
                )}
              </div>

              {owner && (
                <p className="mt-2 text-[13px] font-medium text-slate-500">
                  Drivers linked under{" "}
                  <span className="font-semibold text-slate-800">
                    {ownerName}
                  </span>
                  .
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:w-auto xl:grid-cols-3">
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex h-11 items-center justify-center rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:border-slate-900 hover:bg-slate-900 hover:text-white"
              >
                Back
              </button>

              <button
                type="button"
                onClick={handleOpenLink}
                className="inline-flex h-11 items-center justify-center rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-800 shadow-sm transition-colors hover:border-slate-900 hover:bg-slate-900 hover:text-white"
              >
                Link existing driver
              </button>

              <button
                type="button"
                onClick={handleOpenInvite}
                className="inline-flex h-11 items-center justify-center rounded-xl bg-slate-900 px-5 text-sm font-semibold text-white shadow-[0_10px_28px_rgba(15,23,42,0.45)] transition-transform transition-shadow hover:translate-y-px hover:shadow-[0_8px_20px_rgba(15,23,42,0.45)] active:translate-y-[2px] active:shadow-[0_4px_12px_rgba(15,23,42,0.5)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-900"
              >
                Add driver
              </button>
            </div>
          </div>

          {!loading && !error && totalDrivers > 0 && (
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Total drivers
                </div>
                <div className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">
                  {totalDrivers}
                </div>
              </div>

              <div className="rounded-2xl border border-emerald-200 bg-white px-4 py-3 shadow-sm">
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Verified compliance
                </div>
                <div className="mt-2 text-2xl font-bold text-emerald-700 tabular-nums">
                  {verifiedCount}
                </div>
              </div>

              <div className="rounded-2xl border border-indigo-200 bg-white px-4 py-3 shadow-sm">
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Eligible to assign
                </div>
                <div className="mt-2 text-2xl font-bold text-indigo-700 tabular-nums">
                  {eligibleCount}
                </div>
              </div>
            </div>
          )}

          {loading && (
            <div className="mt-10 grid place-items-center text-sm text-slate-500">
              Loading your drivers…
            </div>
          )}

          {!loading && error && (
            <div className="mt-8 rounded-3xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && totalDrivers === 0 && (
            <div className="mt-8 rounded-3xl border border-dashed border-slate-300 bg-white/80 px-6 py-8 text-center">
              <h2 className="text-sm font-semibold text-slate-900">
                No drivers linked yet
              </h2>
              <p className="mt-2 text-xs text-slate-600">
                Invite a driver by phone number or link an existing Wayroot
                driver profile.
              </p>

              <div className="mt-4 flex flex-col justify-center gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={handleOpenInvite}
                  className="inline-flex h-10 items-center justify-center rounded-xl bg-slate-900 px-5 text-xs font-semibold text-white shadow-[0_12px_30px_rgba(15,23,42,0.45)] hover:translate-y-px hover:shadow-[0_10px_24px_rgba(15,23,42,0.45)]"
                >
                  Invite new driver
                </button>
                <button
                  type="button"
                  onClick={handleOpenLink}
                  className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-300 bg-white px-4 text-xs font-semibold text-slate-800 hover:border-slate-900 hover:bg-slate-900 hover:text-white"
                >
                  Link existing
                </button>
              </div>
            </div>
          )}

          {!loading && !error && totalDrivers > 0 && (
            <div className="mt-6 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 sm:px-5">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Linked drivers
                </div>
                <div className="text-[11px] text-slate-500">
                  {totalDrivers} driver{totalDrivers === 1 ? "" : "s"}
                </div>
              </div>

              <div className="space-y-3 p-4 lg:hidden">
                {drivers.map((d, idx) => renderDriverCard(d, idx))}
              </div>

              <div className="hidden overflow-x-auto lg:block">
                <table className="min-w-full text-xs sm:text-[13px]">
                  <thead className="bg-slate-50 text-slate-700">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium sm:px-5">
                        #
                      </th>
                      <th className="px-4 py-3 text-left font-medium sm:px-5">
                        Driver
                      </th>
                      <th className="px-4 py-3 text-left font-medium sm:px-5">
                        Contact
                      </th>
                      <th className="px-4 py-3 text-left font-medium sm:px-5">
                        License
                      </th>
                      <th className="px-4 py-3 text-left font-medium sm:px-5">
                        Compliance
                      </th>
                      <th className="px-4 py-3 text-left font-medium sm:px-5">
                        Eligible
                      </th>
                      <th className="px-4 py-3 text-left font-medium sm:px-5">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {drivers.map((d, idx) => {
                      const compliance = getDriverComplianceMeta(d);
                      const eligibility = getEligibilityMeta(d);

                      return (
                        <tr
                          key={d.id}
                          className="border-t border-slate-100 hover:bg-slate-50/70"
                        >
                          <td className="px-4 py-3 align-top sm:px-5">
                            <span className="rounded-full bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-600 tabular-nums">
                              {idx + 1}
                            </span>
                          </td>

                          <td className="px-4 py-3 align-top sm:px-5">
                            <div className="text-sm font-semibold text-slate-900">
                              {d.name || "—"}
                            </div>
                            {d.email && (
                              <div className="mt-0.5 break-all text-[11px] text-slate-500">
                                {d.email}
                              </div>
                            )}
                          </td>

                          <td className="px-4 py-3 align-top sm:px-5">
                            <div className="text-[13px] text-slate-900 tabular-nums">
                              {d.phoneNumber || "—"}
                            </div>
                          </td>

                          <td className="px-4 py-3 align-top sm:px-5">
                            <div className="inline-flex items-center rounded-[6px] bg-slate-100 px-2 py-[2px] text-[11px] font-medium text-slate-700">
                              {d.licenseNumber || "Not provided"}
                            </div>
                          </td>

                          <td className="px-4 py-3 align-top sm:px-5">
                            <Pill
                              label={compliance.label}
                              tone={compliance.tone}
                            />
                          </td>

                          <td className="px-4 py-3 align-top sm:px-5">
                            <div className="flex flex-col items-start gap-1.5">
                              <Pill
                                label={eligibility.label}
                                tone={eligibility.tone}
                              />
                              {!eligibility.eligible && eligibility.reason && (
                                <span className="text-[11px] text-slate-500">
                                  {eligibility.reason}
                                </span>
                              )}
                            </div>
                          </td>

                          <td className="px-4 py-3 align-top sm:px-5">
                            <button
                              type="button"
                              onClick={() => handleRemoveDriver(d.id)}
                              disabled={removingId === d.id}
                              className="inline-flex items-center rounded-lg border border-rose-500 px-3 py-1.5 text-[11px] font-medium text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {removingId === d.id ? "Removing…" : "Remove"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>

      {showInviteModal && (
        <div className="fixed inset-0 z-40 bg-slate-900/40 sm:flex sm:items-center sm:justify-center sm:px-4 sm:py-4">
          <div className="h-full w-full overflow-y-auto bg-white shadow-xl sm:h-auto sm:max-h-[90vh] sm:max-w-xl sm:rounded-3xl sm:border sm:border-slate-200">
            <div className="p-4 sm:p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-slate-900">
                    Add new driver
                  </h2>
                  <p className="mt-1 text-[11px] leading-relaxed text-slate-600">
                    Create a new driver profile using their phone number.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  disabled={inviteSubmitting}
                  className="shrink-0 rounded-full border border-slate-300 px-2.5 py-1 text-[11px] text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                >
                  ✕
                </button>
              </div>

              <form className="mt-4 space-y-3" onSubmit={handleInviteSubmit}>
                <div>
                  <label className="text-[11px] font-medium text-slate-700">
                    Name
                  </label>
                  <input
                    type="text"
                    value={inviteForm.name}
                    onChange={(e) =>
                      handleInviteChange("name", e.target.value)
                    }
                    className="mt-1 block w-full rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    required
                  />
                </div>

                <div>
                  <label className="text-[11px] font-medium text-slate-700">
                    Phone number
                  </label>
                  <input
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={inviteForm.phoneNumber}
                    onChange={(e) =>
                      handleInviteChange(
                        "phoneNumber",
                        normalizePhoneInput(e.target.value)
                      )
                    }
                    className="mt-1 block w-full rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    required
                  />
                </div>

                <div>
                  <label className="text-[11px] font-medium text-slate-700">
                    Email (optional)
                  </label>
                  <input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) =>
                      handleInviteChange("email", e.target.value)
                    }
                    className="mt-1 block w-full rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    placeholder="name@example.com"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-medium text-slate-700">
                    License number
                  </label>
                  <input
                    type="text"
                    value={inviteForm.licenseNumber}
                    onChange={(e) =>
                      handleInviteChange(
                        "licenseNumber",
                        e.target.value.toUpperCase()
                      )
                    }
                    className="mt-1 block w-full rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    required
                  />
                </div>

                <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={() => setShowInviteModal(false)}
                    disabled={inviteSubmitting}
                    className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-300 bg-white px-4 text-xs font-semibold text-slate-700 hover:border-slate-900 hover:bg-slate-900 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={inviteSubmitting}
                    className="inline-flex h-10 items-center justify-center rounded-lg bg-slate-900 px-4 text-xs font-semibold text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
                  >
                    {inviteSubmitting ? "Creating…" : "Create driver"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showLinkModal && (
        <div className="fixed inset-0 z-40 bg-slate-900/40 sm:flex sm:items-center sm:justify-center sm:px-4 sm:py-4">
          <div className="h-full w-full overflow-y-auto bg-white shadow-xl sm:h-auto sm:max-h-[90vh] sm:max-w-md sm:rounded-3xl sm:border sm:border-slate-200">
            <div className="p-4 sm:p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-slate-900">
                    Link existing driver
                  </h2>
                  <p className="mt-1 text-[11px] leading-relaxed text-slate-600">
                    Enter the phone number of a driver already signed up in
                    Wayroot.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setShowLinkModal(false)}
                  disabled={linkSubmitting}
                  className="shrink-0 rounded-full border border-slate-300 px-2.5 py-1 text-[11px] text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                >
                  ✕
                </button>
              </div>

              <form className="mt-4 space-y-3" onSubmit={handleLinkSubmit}>
                <div>
                  <label className="text-[11px] font-medium text-slate-700">
                    Driver phone number
                  </label>
                  <input
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={linkPhone}
                    onChange={(e) =>
                      setLinkPhone(normalizePhoneInput(e.target.value))
                    }
                    className="mt-1 block w-full rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    required
                  />
                </div>

                <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={() => setShowLinkModal(false)}
                    disabled={linkSubmitting}
                    className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-300 bg-white px-4 text-xs font-semibold text-slate-700 hover:border-slate-900 hover:bg-slate-900 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={linkSubmitting}
                    className="inline-flex h-10 items-center justify-center rounded-lg bg-slate-900 px-4 text-xs font-semibold text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
                  >
                    {linkSubmitting ? "Linking…" : "Link driver"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ManageDrivers;
